import { memo, useCallback, useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import * as Icons from "@nova/component-icons"
import { ButtonPrimary, ButtonTertiary, ButtonDestructive } from "@nova/component-btn"
import { KeyboardShortcuts, type KeyboardShortcut } from "@nova/component-keyboard-shortcuts"
import { ScrollableArea } from "@nova/component-scrollable-area"
import { GenericInspectorPanelContainer } from "../inspector-panels/GenericInspectorPanel"
import { GenericInspectorPanelHeaderTopRow } from "../inspector-panels/headers/GenericInspectorPanelHeader"
import { GenericInspectorPanelFooterContainer } from "../inspector-panels/footers/GenericInspectorPanelFooter"
import { InspectorPanelAccordion } from "../accordion/InspectorPanelAccordion"
import { selectOrthoMaskByFlightId } from "features/entity-caching/ortho-masks/selectors"
import {
    selectOrthoMaskDrawingType,
    setDrawingType,
    bumpResetKey,
    OrthoMaskDrawingType,
} from "plugins/Orthomosaic/OrthoMaskSlice"
import { deleteOrthoMask } from "features/pubsub/entityActions/cubes/orthoMask"
import { geometryDataStore } from "plugins/shared/layers/GeometryDataStore"
import {
    getGenericConfirmationModalComponent,
    useConfirmationModalContext,
} from "features/modals/WorkspaceConfirmationModalV2"
import classNames from "classnames"

function DrawingTypeButton({
    label,
    Icon,
    isSelected,
    onClick,
    className,
    disabled,
}: {
    label?: string
    Icon: Icons.IconComponent
    isSelected: boolean
    onClick: () => void
    className?: string
    disabled?: boolean
}) {
    return (
        <button
            disabled={disabled}
            className={classNames(
                "h-9 flex grow justify-center items-center cursor-pointer select-none border",
                {
                    "text-gray-0 border-gray-300 bg-gray-700": isSelected,
                    "border-gray-700 text-gray-400 hover:text-gray-300 hover:border-gray-500 active:text-gray-0 active:border-gray-300":
                        !isSelected,
                    "gap-2": !!label,
                },
                className
            )}
            onClick={onClick}
        >
            <Icons.SmallIcon
                Icon={Icon}
                iconClassName={isSelected ? "text-blue-500" : "text-gray-400"}
            />
            {label && <span>{label}</span>}
        </button>
    )
}

function OrthoMaskKeyboardShortcuts({
    isOpen,
    setIsOpen,
    drawingType,
}: {
    isOpen: boolean
    setIsOpen: (isOpen: boolean) => void
    drawingType: OrthoMaskDrawingType
}) {
    let shortcuts: KeyboardShortcut[] = []
    if (drawingType === "rectangle") {
        shortcuts = [
            {
                title: "Draw",
                mac: [Icons.KeyboardLeftClick, Icons.DragPan],
                windows: [Icons.KeyboardLeftClick, Icons.DragPan],
            },
            {
                title: "Square",
                mac: [Icons.KeyboardShift, Icons.DragPan],
                windows: [Icons.KeyboardShift, Icons.DragPan],
            },
        ]
    } else if (drawingType === "polygon") {
        shortcuts = [
            {
                title: "Draw",
                mac: [Icons.KeyboardLeftClick],
                windows: [Icons.KeyboardLeftClick],
            },
        ]
    } else if (drawingType === "edit") {
        shortcuts = [
            {
                title: "Move Vertex",
                mac: [Icons.KeyboardLeftClick, Icons.DragPan],
                windows: [Icons.KeyboardLeftClick, Icons.DragPan],
            },
            {
                title: "Delete Vertex",
                mac: ["delete"],
                windows: [Icons.KeyboardBackspace],
            },
        ]
    }
    if (drawingType !== "rectangle") {
        shortcuts.push(
            {
                title: "Undo",
                mac: [Icons.KeyboardCommand, "Z"],
                windows: ["Ctrl", "Z"],
            },
            {
                title: "Redo",
                mac: [Icons.KeyboardCommand, Icons.KeyboardShift, "Z"],
                windows: ["Ctrl", "Y"],
            },
            {
                title: "Reset",
                mac: [Icons.KeyboardCommand, "delete"],
                windows: ["Ctrl", Icons.KeyboardBackspace],
            },
            {
                title: "Finish",
                mac: ["return"],
                windows: ["Enter"],
            }
        )
    }
    return (
        <InspectorPanelAccordion
            title="Keyboard Shortcuts"
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            shouldPadChildrenVertically={false}
            extraVerticalPaddingInRem={0}
            mutedTitle
        >
            <div className="-mx-2 px-2 my-2 flex flex-col gap-1">
                <KeyboardShortcuts shortcuts={shortcuts} />
            </div>
        </InspectorPanelAccordion>
    )
}

const deleteCropModal = getGenericConfirmationModalComponent({
    title: "Delete Crop?",
    message: "Are you sure you want to delete this drawing?",
    confirmText: "Delete",
    leftConfirmIcon: Icons.Trash,
    ConfirmButtonComponent: ButtonDestructive,
    cancelText: "Cancel",
})

const OrthoMaskPanel = memo(({ flightId }: { flightId: string }) => {
    const dispatch = useAppDispatch()
    const orthoMask = useAppSelector(selectOrthoMaskByFlightId(flightId))
    const drawingType = useAppSelector(selectOrthoMaskDrawingType)
    const drawnPoints = geometryDataStore.useGeometryData("points")
    const [shortcutsOpen, setShortcutsOpen] = useState(false)
    const [lastDrawingTool, setLastDrawingTool] = useState<OrthoMaskDrawingType | undefined>()
    const confirmationModal = useConfirmationModalContext()

    useEffect(() => {
        return () => {
            geometryDataStore.free(false)
        }
    }, [])

    const handleNewDrawing = useCallback(() => {
        geometryDataStore.setAll([])
        dispatch(setDrawingType(lastDrawingTool ?? "polygon"))
    }, [lastDrawingTool, dispatch])

    const handleDrawingTypeChange = useCallback(
        async (newType: OrthoMaskDrawingType) => {
            if (newType === drawingType) return
            setLastDrawingTool(newType)
            geometryDataStore.setAll([])
            dispatch(setDrawingType(newType))
        },
        [drawingType, setLastDrawingTool, dispatch]
    )

    const handleReset = useCallback(() => {
        if (drawingType === "polygon") {
            geometryDataStore.setAll([])
        } else {
            geometryDataStore.setAll(orthoMask?.polygon?.coordinates ?? [])
            dispatch(bumpResetKey())
        }
    }, [dispatch, drawingType, orthoMask])

    const handleCancel = useCallback(() => {
        geometryDataStore.free(false)
    }, [])

    const handleConfirm = useCallback(() => {
        geometryDataStore.free(true)
    }, [])

    const isOriginal =
        drawnPoints.length === orthoMask?.polygon?.coordinates?.length &&
        drawnPoints.every((point, i) => {
            const coord = orthoMask?.polygon?.coordinates[i]
            return coord && coord.longitude === point[0] && coord.latitude === point[1]
        })
    const canReset =
        (drawingType === "polygon" && drawnPoints.length > 0) ||
        (drawingType === "edit" && !!drawnPoints.length && !isOriginal && !!orthoMask)
    const canConfirm = drawnPoints.length >= 3
    const canDelete =
        (drawnPoints.length === 0 || isOriginal) && orthoMask && drawingType === "edit"

    return (
        <GenericInspectorPanelContainer>
            <div className="flex flex-col min-h-0 shrink">
                <div className="px-4 pt-4 pb-2">
                    <GenericInspectorPanelHeaderTopRow
                        typeTitle="Crop Orthomosaic"
                        Icon={Icons.Crop}
                    />
                </div>

                <ScrollableArea shouldPreventScrollbarShifting>
                    <div className="flex flex-col py-2 gap-2">
                        <div className="flex flex-col px-4 gap-2">
                            {drawingType === "edit" ? (
                                <DrawingTypeButton
                                    label="New Drawing"
                                    Icon={Icons.StylusNote}
                                    isSelected={false}
                                    onClick={handleNewDrawing}
                                    className="rounded-lg color-gray-500"
                                />
                            ) : (
                                <>
                                    <small className="xs text-gray-300 select-none">Draw</small>
                                    <div className="flex w-full">
                                        <DrawingTypeButton
                                            label="Polygon"
                                            Icon={Icons.Polyline}
                                            isSelected={drawingType === "polygon"}
                                            onClick={() => handleDrawingTypeChange("polygon")}
                                            className="rounded-l-[8px]"
                                        />
                                        <DrawingTypeButton
                                            label="Rectangle"
                                            Icon={Icons.Rectangle}
                                            isSelected={drawingType === "rectangle"}
                                            onClick={() => handleDrawingTypeChange("rectangle")}
                                            className="rounded-r-[8px]"
                                        />
                                    </div>
                                </>
                            )}

                            {canReset ? (
                                <DrawingTypeButton
                                    label="Reset"
                                    Icon={Icons.RestartAlt}
                                    isSelected={false}
                                    onClick={handleReset}
                                    className="rounded-lg color-gray-500"
                                />
                            ) : (
                                <div />
                            )}
                        </div>

                        <OrthoMaskKeyboardShortcuts
                            isOpen={shortcutsOpen}
                            setIsOpen={setShortcutsOpen}
                            drawingType={drawingType}
                        />
                    </div>
                </ScrollableArea>

                <GenericInspectorPanelFooterContainer>
                    <div className="flex justify-between">
                        {canDelete ? (
                            <ButtonDestructive
                                LeftIcon={Icons.Trash}
                                className="!pl-3 min-w-[52px]"
                                onClick={async () => {
                                    const confirmed = await confirmationModal.confirm({
                                        RenderComponent: deleteCropModal,
                                    })
                                    if (!confirmed) return
                                    dispatch(deleteOrthoMask({ id: orthoMask.id }))
                                    geometryDataStore.free(false)
                                }}
                                isTransparent
                            />
                        ) : (
                            <div className="px-4"></div>
                        )}
                        <div className="flex gap-2 justify-end">
                            <ButtonTertiary title="Cancel" onClick={handleCancel} />
                            <ButtonPrimary
                                title="Confirm"
                                RightIcon={Icons.Checkmark}
                                disabled={!canConfirm}
                                onClick={handleConfirm}
                            />
                        </div>
                    </div>
                </GenericInspectorPanelFooterContainer>
            </div>
        </GenericInspectorPanelContainer>
    )
})
OrthoMaskPanel.displayName = "OrthoMaskPanel"
export { OrthoMaskPanel }
